/**
 * Custom Application Error Classes
 *
 * Base error class and specialized error types for the ToolLife application.
 * All custom errors extend AppError for consistent error handling.
 *
 * @module lib/errors/AppError
 */

/**
 * Error categories for classification
 */
export const ErrorCategory = {
  // Network/Communication Errors
  NETWORK_ERROR: 'network',
  API_ERROR: 'api',
  TIMEOUT_ERROR: 'timeout',

  // Authentication/Authorization Errors
  AUTH_ERROR: 'auth',
  PERMISSION_ERROR: 'permission',
  SESSION_ERROR: 'session',

  // Validation Errors
  VALIDATION_ERROR: 'validation',

  // Application Errors
  RENDER_ERROR: 'render',
  STATE_ERROR: 'state',
  CONFIG_ERROR: 'config',

  // Device/Hardware Errors
  CAMERA_ERROR: 'camera',
  DEVICE_ERROR: 'device',

  // Data Errors
  DATA_ERROR: 'data',
  PARSE_ERROR: 'parse',

  // Unknown
  UNKNOWN_ERROR: 'unknown',
} as const;

export type ErrorCategory = (typeof ErrorCategory)[keyof typeof ErrorCategory];

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  CRITICAL: 'critical', // App cannot continue
  ERROR: 'error', // Operation failed
  WARNING: 'warning', // Non-blocking issue
  INFO: 'info', // Informational message
} as const;

export type ErrorSeverity = (typeof ErrorSeverity)[keyof typeof ErrorSeverity];

/** `Error.captureStackTrace` is a V8 extension, so it is absent from the DOM/ES typings. */
type V8ErrorConstructor = ErrorConstructor & {
  captureStackTrace?: (target: object, constructorOpt?: unknown) => void;
};

/**
 * Base application error class
 * All custom errors should extend this class
 */
export class AppError extends Error {
  public readonly category: ErrorCategory;
  public readonly severity: ErrorSeverity;
  public readonly timestamp: string;
  public readonly code?: string | number;
  public readonly details?: Record<string, unknown>;
  public readonly cause?: Error;

  constructor(
    message: string,
    category: ErrorCategory = ErrorCategory.UNKNOWN_ERROR,
    severity: ErrorSeverity = ErrorSeverity.ERROR,
    options?: {
      code?: string | number;
      details?: Record<string, unknown>;
      cause?: Error;
    }
  ) {
    super(message);

    this.name = this.constructor.name;
    this.category = category;
    this.severity = severity;
    this.timestamp = new Date().toISOString();
    this.code = options?.code;
    this.details = options?.details;
    this.cause = options?.cause;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    (Error as V8ErrorConstructor).captureStackTrace?.(this, this.constructor);
  }

  /**
   * Convert error to a plain object for logging
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      category: this.category,
      severity: this.severity,
      timestamp: this.timestamp,
      code: this.code,
      details: this.details,
      stack: this.stack,
    };
  }
}

/**
 * API/Backend errors (HTTP errors, API failures)
 */
export class ApiError extends AppError {
  public readonly statusCode?: number;
  public readonly endpoint?: string;

  constructor(
    message: string = 'API error occurred',
    statusCode?: number,
    options?: {
      endpoint?: string;
      code?: string | number;
      details?: Record<string, unknown>;
      cause?: Error;
    }
  ) {
    super(
      message,
      ErrorCategory.API_ERROR,
      statusCode && statusCode >= 500 ? ErrorSeverity.CRITICAL : ErrorSeverity.ERROR,
      options
    );

    this.statusCode = statusCode;
    this.endpoint = options?.endpoint;
  }

  toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      statusCode: this.statusCode,
      endpoint: this.endpoint,
    };
  }
}

/**
 * Authentication errors (login failures, invalid credentials)
 */
export class AuthError extends AppError {
  constructor(
    message: string = 'Authentication failed',
    options?: {
      code?: string | number;
      details?: Record<string, unknown>;
      cause?: Error;
    }
  ) {
    super(message, ErrorCategory.AUTH_ERROR, ErrorSeverity.ERROR, options);
  }
}

/**
 * Camera-related errors (access denied, not found, capture failed)
 */
export class CameraError extends AppError {
  public readonly cameraType?: 'generic' | 'keyence';

  constructor(
    message: string = 'Camera error occurred',
    cameraType?: 'generic' | 'keyence',
    options?: {
      code?: string | number;
      details?: Record<string, unknown>;
      cause?: Error;
    }
  ) {
    super(message, ErrorCategory.CAMERA_ERROR, ErrorSeverity.ERROR, options);
    this.cameraType = cameraType;
  }

  toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      cameraType: this.cameraType,
    };
  }
}
